import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Sidebar from '../components/Sidebar';
import { toast } from 'sonner';
import { extractErrorMessage } from '../lib/errorUtils';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Shield, ShieldCheck, ShieldOff, Flame, Smartphone, QrCode, Copy, KeyRound } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Security = ({ user, onLogout, theme, onToggleTheme }) => {
  const { t } = useLanguage();
  const [collapsed, setCollapsed] = useState(false);

  // 2FA state
  const [totpEnabled, setTotpEnabled] = useState(false);
  const [setupOpen, setSetupOpen] = useState(false);
  const [setupData, setSetupData] = useState(null);
  const [setupCode, setSetupCode] = useState('');
  const [disableOpen, setDisableOpen] = useState(false);
  const [disablePassword, setDisablePassword] = useState('');
  const [disableCode, setDisableCode] = useState('');
  const [busy, setBusy] = useState(false);

  // Factory reset state
  const [showReset, setShowReset] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetPassword, setResetPassword] = useState('');
  const [resetName, setResetName] = useState('');
  const [resetConfirm, setResetConfirm] = useState('');
  const [resetting, setResetting] = useState(false);

  // Own password change
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [newPassword2, setNewPassword2] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);

  const isAdmin = user?.role === 'admin';

  const isDark = theme === 'dark';
  const cardBg = isDark ? '#161616' : '#FFFFFF';
  const bgSubtle = isDark ? '#0A0A0A' : '#F8F9FA';
  const textPrimary = isDark ? '#F4F4F4' : '#161616';
  const textSecondary = isDark ? '#A8A8A8' : '#525252';
  const borderColor = isDark ? '#262626' : '#E0E0E0';

  const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/auth/2fa/status`, { headers: authHeader() });
        setTotpEnabled(!!data.totp_enabled);
      } catch (e) { console.debug('2FA status fetch failed', e?.response?.status || e?.message); }
    })();
  }, []);

  const startSetup = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/auth/2fa/setup`, {}, { headers: authHeader() });
      setSetupData(data);
      setSetupCode('');
      setSetupOpen(true);
    } catch (e) {
      toast.error(extractErrorMessage(e, t('sec_2fa_setup_failed')));
    } finally {
      setBusy(false);
    }
  };

  const confirmEnable = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/auth/2fa/enable`, { code: setupCode.trim() }, { headers: authHeader() });
      toast.success(t('sec_2fa_enabled_ok'));
      setTotpEnabled(true);
      setSetupOpen(false);
      setSetupData(null);
    } catch (e) {
      toast.error(extractErrorMessage(e, t('sec_2fa_bad_code')));
    } finally {
      setBusy(false);
    }
  };

  const confirmDisable = async () => {
    setBusy(true);
    try {
      await axios.post(
        `${API}/auth/2fa/disable`,
        { password: disablePassword, code: disableCode.trim() },
        { headers: authHeader() }
      );
      toast.success(t('sec_2fa_disabled_ok'));
      setTotpEnabled(false);
      setDisableOpen(false);
      setDisablePassword('');
      setDisableCode('');
    } catch (e) {
      toast.error(extractErrorMessage(e, t('sec_2fa_bad_code')));
    } finally {
      setBusy(false);
    }
  };

  const handleFactoryReset = async () => {
    setResetting(true);
    try {
      await axios.post(
        `${API}/admin/factory-reset`,
        {
          confirmation: resetConfirm,
          new_email: resetEmail,
          new_password: resetPassword,
          new_full_name: resetName,
        },
        { headers: authHeader() }
      );
      toast.success(t('sec_reset_done'));
      setShowReset(false);
      setTimeout(() => {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        window.location.href = '/login';
      }, 1200);
    } catch (e) {
      toast.error(extractErrorMessage(e, t('sec_reset_failed')));
    } finally {
      setResetting(false);
    }
  };

  const handleChangePassword = async () => {
    if (newPassword.length < 8) {
      toast.error(t('sec_pwd_min'));
      return;
    }
    if (newPassword !== newPassword2) {
      toast.error(t('sec_pwd_mismatch'));
      return;
    }
    setChangingPassword(true);
    try {
      await axios.post(
        `${API}/auth/change-password`,
        { old_password: oldPassword, new_password: newPassword },
        { headers: authHeader() }
      );
      toast.success(t('sec_pwd_changed'));
      setOldPassword('');
      setNewPassword('');
      setNewPassword2('');
    } catch (e) {
      toast.error(extractErrorMessage(e, t('sec_pwd_error')));
    } finally {
      setChangingPassword(false);
    }
  };

  const copySecret = () => {
    if (setupData?.secret) {
      navigator.clipboard.writeText(setupData.secret).then(() => toast.success(t('sec_2fa_secret_copied')));
    }
  };

  return (
    <div className="flex h-screen overflow-hidden">
      <Sidebar user={user} onLogout={onLogout} theme={theme} onToggleTheme={onToggleTheme} collapsed={collapsed} onToggle={() => setCollapsed(!collapsed)} />
      <div className="flex-1 overflow-y-auto" style={{ backgroundColor: bgSubtle }}>
        <div className="p-6 lg:p-8 max-w-4xl">
          <div className="mb-8">
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-2 flex items-center gap-3" style={{ color: textPrimary }} data-testid="security-title">
              <Shield size={36} style={{ color: '#0F62FE' }} />
              {t('sec_title')}
            </h1>
            <p className="text-base" style={{ color: textSecondary }}>{t('sec_subtitle_2fa')}</p>
          </div>

          {/* 2FA section */}
          <div className="p-6 rounded-lg border" style={{ backgroundColor: cardBg, borderColor }}>
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  {totpEnabled ? <ShieldCheck size={22} style={{ color: '#198038' }} /> : <ShieldOff size={22} style={{ color: '#A8A8A8' }} />}
                  <h2 className="text-xl font-bold" style={{ color: textPrimary }}>{t('sec_2fa_title')}</h2>
                </div>
                <p className="text-sm" style={{ color: textSecondary }}>{t('sec_2fa_desc')}</p>
                <div className="mt-3 flex items-center gap-2 p-2 rounded" style={{ backgroundColor: totpEnabled ? '#19803815' : '#F1C21B20' }}>
                  {totpEnabled ? (
                    <span className="text-xs font-semibold" style={{ color: '#198038' }} data-testid="2fa-status-on">
                      {t('sec_2fa_status_on')}
                    </span>
                  ) : (
                    <span className="text-xs font-semibold" style={{ color: '#B58900' }} data-testid="2fa-status-off">
                      {t('sec_2fa_status_off')}
                    </span>
                  )}
                </div>
              </div>
              <div>
                {totpEnabled ? (
                  <Button variant="outline" onClick={() => setDisableOpen(true)} data-testid="2fa-disable-button">
                    <ShieldOff size={14} className="mr-2" />
                    {t('sec_2fa_disable')}
                  </Button>
                ) : (
                  <Button className="btn-primary" onClick={startSetup} disabled={busy} data-testid="2fa-enable-button">
                    <Smartphone size={14} className="mr-2" />
                    {t('sec_2fa_enable')}
                  </Button>
                )}
              </div>
            </div>
          </div>

          {/* Change my password */}
          <div className="p-6 rounded-lg border mt-6" style={{ backgroundColor: cardBg, borderColor }}>
            <div className="flex items-center gap-2 mb-3">
              <KeyRound size={22} style={{ color: '#0F62FE' }} />
              <h2 className="text-xl font-bold" style={{ color: textPrimary }}>{t('sec_change_password')}</h2>
            </div>
            <p className="text-sm mb-4" style={{ color: textSecondary }}>{t('sec_change_password_desc')}</p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
              <div>
                <Label>{t('sec_current_password')}</Label>
                <Input type="password" value={oldPassword} onChange={(e) => setOldPassword(e.target.value)} className="mt-1" data-testid="pwd-old-input" />
              </div>
              <div>
                <Label>{t('sec_new_password')}</Label>
                <Input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} className="mt-1" data-testid="pwd-new-input" placeholder="min 8" />
              </div>
              <div>
                <Label>{t('sec_confirm_password')}</Label>
                <Input type="password" value={newPassword2} onChange={(e) => setNewPassword2(e.target.value)} className="mt-1" data-testid="pwd-confirm-input" />
              </div>
            </div>
            <div className="mt-4 flex justify-end">
              <Button
                className="btn-primary"
                onClick={handleChangePassword}
                disabled={changingPassword || !oldPassword || newPassword.length < 8 || newPassword !== newPassword2}
                data-testid="pwd-submit-button"
              >
                <KeyRound size={14} className="mr-2" />
                {changingPassword ? t('sec_pwd_saving') : t('sec_pwd_save')}
              </Button>
            </div>
          </div>

          {/* Danger Zone — admin only */}
          {isAdmin && (
          <div className="p-6 rounded-lg border-2 mt-6" style={{ backgroundColor: isDark ? '#2a0d0d' : '#FFF5F5', borderColor: '#DA1E28' }}>
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div>
                <h2 className="text-xl font-bold flex items-center gap-2" style={{ color: '#DA1E28' }}>
                  <Flame size={22} />
                  {t('sec_danger_zone')}
                </h2>
                <p className="text-sm mt-1" style={{ color: isDark ? '#FFB3B3' : '#8A0F1B' }}>
                  {t('sec_reset_desc')}
                </p>
              </div>
              <Button
                variant="destructive"
                onClick={() => {
                  setResetEmail('admin@crmdragonfruit.online');
                  setResetPassword('');
                  setResetName('Admin');
                  setResetConfirm('');
                  setShowReset(true);
                }}
                data-testid="factory-reset-button"
                style={{ backgroundColor: '#DA1E28', color: 'white' }}
              >
                <Flame size={16} className="mr-2" />
                {t('sec_reset_start')}
              </Button>
            </div>
          </div>
          )}
        </div>
      </div>

      {/* 2FA Setup Dialog */}
      <Dialog open={setupOpen} onOpenChange={(open) => { setSetupOpen(open); if (!open) { setSetupData(null); setSetupCode(''); } }}>
        <DialogContent className="max-w-lg" data-testid="2fa-setup-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <QrCode size={20} /> {t('sec_2fa_setup_title')}
            </DialogTitle>
            <DialogDescription>{t('sec_2fa_setup_step1')}</DialogDescription>
          </DialogHeader>
          {setupData && (
            <div className="space-y-4 mt-2">
              <div className="flex justify-center">
                <img src={setupData.qr_data_uri} alt="2FA QR Code" width={220} height={220} data-testid="2fa-qr-image" style={{ background: 'white', padding: 8, borderRadius: 6 }} />
              </div>
              <div>
                <Label className="text-xs">{t('sec_2fa_manual_secret')}</Label>
                <div className="flex items-center gap-2 mt-1">
                  <Input value={setupData.secret} readOnly className="font-mono text-sm" data-testid="2fa-secret" />
                  <Button size="sm" variant="outline" onClick={copySecret} data-testid="2fa-copy-secret">
                    <Copy size={14} />
                  </Button>
                </div>
              </div>
              <div>
                <Label>{t('sec_2fa_enter_code')}</Label>
                <Input
                  value={setupCode}
                  onChange={(e) => setSetupCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="123456"
                  inputMode="numeric"
                  maxLength={6}
                  className="mt-1 text-2xl font-mono tracking-widest text-center"
                  data-testid="2fa-setup-code-input"
                />
              </div>
            </div>
          )}
          <DialogFooter className="mt-3">
            <Button variant="outline" onClick={() => setSetupOpen(false)}>{t('cancel') || 'Annulla'}</Button>
            <Button
              className="btn-primary"
              onClick={confirmEnable}
              disabled={busy || setupCode.length !== 6}
              data-testid="2fa-setup-confirm"
            >
              <ShieldCheck size={14} className="mr-2" />
              {t('sec_2fa_confirm_enable')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 2FA Disable Dialog */}
      <Dialog open={disableOpen} onOpenChange={setDisableOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{t('sec_2fa_disable_title')}</DialogTitle>
            <DialogDescription>{t('sec_2fa_disable_desc')}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <div>
              <Label>{t('password')}</Label>
              <Input type="password" value={disablePassword} onChange={(e) => setDisablePassword(e.target.value)} data-testid="2fa-disable-password" className="mt-1" />
            </div>
            <div>
              <Label>{t('sec_2fa_current_code')}</Label>
              <Input
                value={disableCode}
                onChange={(e) => setDisableCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                inputMode="numeric"
                maxLength={6}
                data-testid="2fa-disable-code"
                className="mt-1 text-xl font-mono tracking-widest text-center"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDisableOpen(false)}>{t('cancel') || 'Annulla'}</Button>
            <Button variant="destructive" onClick={confirmDisable} disabled={busy || !disablePassword || disableCode.length !== 6} data-testid="2fa-disable-confirm">
              {t('sec_2fa_confirm_disable')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Factory Reset Dialog */}
      <Dialog open={showReset} onOpenChange={setShowReset}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" style={{ color: '#DA1E28' }}>
              <Flame size={20} />
              {t('sec_reset_dialog_title')}
            </DialogTitle>
            <DialogDescription>{t('sec_reset_dialog_desc')}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <div className="p-3 rounded text-sm" style={{ backgroundColor: '#DA1E2810', border: '1px solid #DA1E2840', color: isDark ? '#FFB3B3' : '#8A0F1B' }}>
              <strong>{t('sec_reset_warning_title')}</strong>
              <ul className="list-disc list-inside mt-1 space-y-0.5">
                <li>{t('sec_reset_will_delete_leads')}</li>
                <li>{t('sec_reset_will_delete_users')}</li>
                <li>{t('sec_reset_will_rotate_admin')}</li>
              </ul>
            </div>
            <div>
              <Label>{t('sec_reset_new_email')}</Label>
              <Input value={resetEmail} onChange={(e) => setResetEmail(e.target.value)} type="email" className="mt-1" data-testid="reset-email-input" />
            </div>
            <div>
              <Label>{t('sec_reset_new_name')}</Label>
              <Input value={resetName} onChange={(e) => setResetName(e.target.value)} className="mt-1" data-testid="reset-name-input" />
            </div>
            <div>
              <Label>{t('sec_reset_new_password')} <span className="opacity-60 text-xs">(min. 8)</span></Label>
              <Input value={resetPassword} onChange={(e) => setResetPassword(e.target.value)} type="text" className="mt-1 font-mono" placeholder="Dragon@Admin2026!" data-testid="reset-password-input" />
            </div>
            <div>
              <Label>{t('sec_reset_type_confirm')} <span className="font-mono font-bold">RESET-CRM-DATA</span></Label>
              <Input value={resetConfirm} onChange={(e) => setResetConfirm(e.target.value)} className="mt-1 font-mono" placeholder="RESET-CRM-DATA" data-testid="reset-confirm-input" />
            </div>
          </div>
          <DialogFooter className="mt-3">
            <Button variant="outline" onClick={() => setShowReset(false)} data-testid="reset-cancel-button">
              {t('cancel') || 'Annulla'}
            </Button>
            <Button
              disabled={resetting || resetConfirm !== 'RESET-CRM-DATA' || !resetEmail || resetPassword.length < 8 || !resetName}
              onClick={handleFactoryReset}
              data-testid="reset-submit-button"
              style={{ backgroundColor: '#DA1E28', color: 'white' }}
            >
              <Flame size={14} className="mr-2" />
              {resetting ? t('sec_resetting') : t('sec_reset_confirm_button')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Security;
